import { useEffect, useState } from 'react'

import { useIncidentCreationData } from '../hooks/useIncidentCreationData'
import { UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'

import { Box, ModalBody, Text } from '@chakra-ui/react'

import {
  FacilityOptionLabel,
  FormInputControl,
  FormInputSelectControl,
  FormTextareaControl,
} from '@/components/ui'
import { FacilityType } from '@/graphql/generated/schemas'
import {
  createFacilityAddressFilter,
  getIncidentCategoryOptions,
  getIncidentFacilityOptionsWithAddress,
} from '@/utils/forms/optionBuilders'
import {
  MISCELLANEOUS,
  sortOptions,
  sortOptionsWithLabelLast,
} from '@/utils/forms/optionHelpers'
import {
  SEARCH_DEBOUNCE_TIME,
  getFacilityNoOptionsMessage,
} from '@/utils/forms/validation'

import { FormInputsIProps } from '../types/types'
import { MAX_INCIDENT_FILES } from '../utils/constants'
import {
  descriptionRules,
  facilityNameRules,
  incidentCategoryRules,
  nameRules,
} from '../utils/utils'
import { AttachFilesButton } from './AttachFilesButton'
import { MarketIncidentForm } from './MarketIncidentForm'
import { NormalIncidentForm } from './NormalIncidentForm'
import { SelectedFilesPreview } from './SelectedFilesPreview'

interface IncidentModalBodyIProps {
  form: UseFormMethods<FormInputsIProps>
  selectedAttachments: File[]
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFileRemove: (file: File) => void
}

export const IncidentModalBody = ({
  form,
  selectedAttachments,
  onFileSelect,
  onFileRemove,
}: IncidentModalBodyIProps) => {
  const { t } = useTranslation()
  const { watch, register, getValues, reset, control, errors } = form
  const [facility, setFacility] = useState(null)
  const [facilityName, setFacilityName] = useState('')
  const [debouncedfacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )

  const {
    facilitiesData,
    isFacilitiesLoading,
    isFacilitiesError,
    categoriesData,
  } = useIncidentCreationData({
    facilityName: debouncedfacilityName,
  })
  const facilityOptions = getIncidentFacilityOptionsWithAddress(
    facilitiesData,
    true,
    debouncedfacilityName
  )
  const incidentCategories = sortOptionsWithLabelLast(
    sortOptions(getIncidentCategoryOptions(categoriesData)),
    MISCELLANEOUS
  )

  const watchFacilityOrigin = watch('facilityOrigin')
  const shouldShowMarketFlow = facility?.type === FacilityType.Market

  useEffect(() => {
    if (watchFacilityOrigin) {
      const selectedFacility = facilitiesData?.facilities?.edges?.find(
        (f) => f?.node?.id == watchFacilityOrigin?.value
      )
      setFacility(selectedFacility?.node)
      reset({
        ...getValues(),
        floorOrigin: null,
        deviceOrigin: null,
      })
    }
  }, [watchFacilityOrigin])

  return (
    <ModalBody p={0}>
      <Box px={6} py={2}>
        <Box mb={3}>
          <FormInputControl
            data-testid='createIncidentModal_incidentName'
            errorMessage={errors.name && errors.name.message}
            id='name'
            inputRef={register(nameRules)}
            isInvalid={!!errors.name}
            label='Incident Name (optional)'
            placeholder='Enter Incident Name'
          />
        </Box>
        <Box mb={3}>
          <FormInputSelectControl
            control={control}
            dataTestId='createIncidentModal_facilityName'
            defaultValue={null}
            // Filters by facility name, short name, and address.
            // Without filterOption, React Select defaults to label-only search.
            // To modify searchable fields, update createFacilityAddressFilter.
            // https://react-select.com/advanced
            filterOption={createFacilityAddressFilter}
            formatOptionLabel={(option, meta) => (
              <FacilityOptionLabel
                isValueContext={meta?.context === 'value'}
                option={option}
              />
            )}
            id='facilityOrigin'
            isInvalid={!!errors.facilityOrigin}
            isLoading={isFacilitiesLoading}
            label='Facility Name *'
            noOptionsMessage={({ inputValue }) =>
              getFacilityNoOptionsMessage(
                inputValue,
                isFacilitiesError,
                t('common:facility_search_error')
              )
            }
            onInputChange={setFacilityName}
            options={facilityOptions}
            placeholder='Select the Facility of Origin'
            rules={facilityNameRules}
          />
        </Box>
        <Box mb={3}>
          <FormInputSelectControl
            control={control}
            dataTestId='createIncidentModal_incidentCategory'
            defaultValue={null}
            id='incidentCategory'
            isInvalid={!!errors.incidentCategory}
            label='Incident Category *'
            options={incidentCategories}
            placeholder='Select Incident Category'
            rules={incidentCategoryRules}
          />
        </Box>
        {shouldShowMarketFlow ? (
          <MarketIncidentForm form={form} />
        ) : (
          <NormalIncidentForm facility={facility} form={form} />
        )}
        <Box mb={3}>
          <FormTextareaControl
            data-testid='createIncidentModal_description'
            errorMessage={errors.description && errors.description.message}
            id='description'
            isInvalid={!!errors.description}
            label='Incident Description (optional)'
            labelFontWeight='500'
            placeholder='Enter Incident Description'
            textareaRef={register(descriptionRules)}
          />
        </Box>
        <Box mb={3}>
          <Text
            color='gray.800'
            fontSize='md'
            fontWeight='medium'
            mb={2}
            opacity={0.5}
          >
            Attachments (optional)
          </Text>
          <AttachFilesButton
            handleFileSelect={onFileSelect}
            maxFiles={MAX_INCIDENT_FILES}
            selectedFiles={selectedAttachments}
          />
          {selectedAttachments.length > 0 && (
            <SelectedFilesPreview
              removeFile={onFileRemove}
              selectedFiles={selectedAttachments}
            />
          )}
        </Box>
      </Box>
    </ModalBody>
  )
}
